'use client';

import { useState, type ReactNode } from 'react';

import type { PartnersAdvisor as Advisor } from './partners-types';
import { CmsText, TAG } from './partners-ui';

/** Odpoveď 1 hovorí za model B (−1), 2 je nerozhodná (0), 3 hovorí za model A (+1). */
const SCORES = [-1, 0, 1];

/** Model A/B pri jasnej prevahe (pri troch otázkach súčet ±2, ako v návrhu), inak kombinácia. */
function recommend(advisor: Advisor, answers: Record<string, number>) {
  const scores = advisor.questions.map(question => answers[question.id]);
  if (!scores.length || scores.some(score => score === undefined)) return advisor.initial;
  const total = scores.reduce((sum, score) => sum + score, 0);
  const threshold = Math.ceil((2 * scores.length) / 3);
  if (total >= threshold) return advisor.outcomes.a;
  if (total <= -threshold) return advisor.outcomes.b;
  return advisor.outcomes.mix;
}

/** Poradca „Ktorý model sa vám hodí" – tri otázky, odporúčanie po zodpovedaní všetkých. */
export function PartnersAdvisor({ advisor, head }: { advisor: Advisor; head: ReactNode }) {
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const advice = recommend(advisor, answers);

  return (
    <>
      <div>
        {head}
        <form onSubmit={event => event.preventDefault()}>
          {advisor.questions.map(question => (
            <fieldset
              key={question.id}
              className="m-0 min-w-0 border-t border-solid border-white/[.16] px-0 py-5"
            >
              <legend className="float-left mb-3 w-full p-0 text-[17px] font-semibold text-white">
                {question.question}
              </legend>
              <div className="clear-both flex flex-wrap gap-2">
                {question.answers.map((answer, index) =>
                  answer ? (
                    <label key={index} className="cursor-pointer">
                      <input
                        type="radio"
                        name={question.id}
                        value={SCORES[index]}
                        checked={answers[question.id] === SCORES[index]}
                        onChange={() =>
                          setAnswers(current => ({ ...current, [question.id]: SCORES[index] }))
                        }
                        className="peer pointer-events-none absolute opacity-0"
                      />
                      <span className="inline-block border border-solid border-white/[.28] px-4 py-[10px] text-[15px] [transition:background_.15s,border-color_.15s] peer-checked:border-accent peer-checked:bg-accent peer-checked:font-semibold peer-checked:text-black peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-white">
                        {answer}
                      </span>
                    </label>
                  ) : null,
                )}
              </div>
            </fieldset>
          ))}
        </form>
      </div>
      <div
        aria-live="polite"
        className="flex flex-col gap-[14px] self-start bg-white p-8 text-[#161412]"
      >
        <span className={TAG}>{advisor.tag}</span>
        <span className="text-[24px] font-bold leading-[1.2] tracking-[-.02em]">
          {advice.title}
        </span>
        <CmsText html={advice.text} className="text-[#4a4744]" />
        <a
          href="#kontakt-partneri"
          className="font-semibold text-accent underline underline-offset-[3px]"
        >
          {advisor.link}
        </a>
      </div>
    </>
  );
}
